import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import axios from 'axios';
import { AuthGuard } from '@/components/Auth/AuthGuard';
import { getApiBaseUrl } from '@/services/api';
import { auth, getAuthToken } from '@/services/firebase';
import { Camera, Loader2, CheckCircle2, XCircle } from 'lucide-react';

export const Route = createFileRoute('/auth/extension')({
  component: () => <AuthGuard><ExtensionAuthPage /></AuthGuard>,
});

// 4K Labs Capture's id is fixed by its manifest key: no other extension can ask the person for an upload key.
const CHROMIUM_REDIRECT = /^https:\/\/kjajbldgpjdoeknbgfecgiemnfhgngda\.chromiumapp\.org\/?$/;

type Status = 'asking' | 'working' | 'success' | 'error';

function redirectProblem(redirect: string | null): string | null {
  if (!redirect) return 'Missing redirect URL. This page is meant to be opened by the 4K Labs Capture extension.';
  if (!CHROMIUM_REDIRECT.test(redirect)) return 'Invalid redirect URL. Expected 4K Labs Capture\'s sign-in address.';
  return null;
}

function ExtensionAuthPage() {
  const [redirect] = useState(() => new URLSearchParams(window.location.search).get('ext_redirect'));
  const problem = redirectProblem(redirect);
  const [status, setStatus] = useState<Status>(problem ? 'error' : 'asking');
  const [message, setMessage] = useState(problem ?? '');
  const email: string | null = auth?.currentUser?.email ?? null;

  function returnToExtension(query: string) {
    window.location.replace(`${redirect}${redirect!.endsWith('/') ? '' : '/'}?${query}`);
  }

  async function allow() {
    setStatus('working');
    setMessage('Connecting extension…');
    try {
      const token = await getAuthToken();
      const res = await axios.post(
        `${getApiBaseUrl()}/pats`,
        {
          name: `4K Labs Capture (${new Date().toISOString().slice(0, 10)})`,
          expiresInDays: 180,
          scopes: ['captures:write'],
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const extensionToken = res.data?.token;
      if (!extensionToken) throw new Error('Server did not return a token');

      setStatus('success');
      setMessage('Connected. Returning to the extension…');
      returnToExtension(`token=${encodeURIComponent(extensionToken)}`);
    } catch (err) {
      setStatus('error');
      setMessage(
        err instanceof Error
          ? `Failed to connect extension: ${err.message}`
          : 'Failed to connect extension.',
      );
    }
  }

  const Icon = status === 'success' ? CheckCircle2 : status === 'error' ? XCircle : Loader2;
  const tint = status === 'success' ? 'text-green-500' : status === 'error' ? 'text-red-500' : 'text-primary';

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">4K Labs Capture</h1>
            <p className="text-xs text-zinc-500">Connecting extension</p>
          </div>
        </div>
        {status === 'asking' ? (
          <>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Allow 4K Labs Capture to upload captures to your account?</p>
              {email && (
                <p className="text-xs text-zinc-400">
                  Signed in as <span className="text-zinc-200">{email}</span>
                </p>
              )}
              <p className="text-xs text-zinc-500">
                It can upload captures and nothing else. You can remove it any time in Settings, API &amp; connector.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => returnToExtension('error=cancelled')}
                className="h-9 rounded-lg border border-zinc-700 px-4 text-sm font-medium text-zinc-300 hover:bg-zinc-800 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={allow}
                className="h-9 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition"
              >
                Allow
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-3 text-sm">
            <Icon className={`w-5 h-5 ${tint} ${status === 'working' ? 'animate-spin' : ''}`} />
            <span>{message}</span>
          </div>
        )}
        {status === 'error' && (
          <p className="text-xs text-zinc-500">
            Close this tab and try clicking Connect in the extension popup again.
          </p>
        )}
      </div>
    </div>
  );
}
